import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, Home, Users, TrendingUp, DollarSign, Building, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  const getDateRange = useCallback(() => {
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'week':
        return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'month':
        return { start: format(startOfMonth(today), 'yyyy-MM-dd'), end: format(endOfMonth(today), 'yyyy-MM-dd') };
      case '3months':
        return { start: format(subMonths(today, 3), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case '6months':
        return { start: format(subMonths(today, 6), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'year':
        return { start: format(startOfYear(today), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        return { start: null, end: null };
    }
  }, [dateRange, customStartDate, customEndDate]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      let url = `${API_URL}/api/stats`;
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token, getDateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value === 'custom') {
      setShowCustomDates(true);
    } else {
      setShowCustomDates(false);
    }
  };

  const formatCurrency = (amount) => {
    return `AZN ${(amount || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2 })}`;
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today': return 'Bugün';
      case 'week': return 'Son 7 gün';
      case 'month': return 'Bu ay';
      case '3months': return 'Son 3 ay';
      case '6months': return 'Son 6 ay';
      case 'year': return 'Bu il';
      case 'custom': return customStartDate && customEndDate ? `${customStartDate} - ${customEndDate}` : 'Xüsusi tarix';
      default: return 'Bütün vaxt';
    }
  };

  const statCards = [
    {
      title: t('admin_cottages'),
      value: stats?.cottage_count || 0,
      icon: Building,
      color: 'bg-[#2D5016]',
      textColor: 'text-[#2D5016]'
    },
    {
      title: t('admin_customers'),
      value: stats?.customer_count || 0,
      icon: Users,
      color: 'bg-[#D97706]',
      textColor: 'text-[#D97706]'
    },
    {
      title: t('admin_reservations'),
      value: stats?.reservation_count || 0,
      icon: Calendar,
      color: 'bg-blue-500',
      textColor: 'text-blue-500'
    },
    {
      title: t('stats_total_deposit'),
      value: formatCurrency(stats?.total_deposits),
      icon: DollarSign,
      color: 'bg-green-500',
      textColor: 'text-green-500'
    },
    {
      title: t('stats_total_reservations'),
      value: formatCurrency(stats?.total_reservations),
      icon: TrendingUp,
      color: 'bg-purple-500',
      textColor: 'text-purple-500'
    },
    {
      title: t('stats_remaining'),
      value: formatCurrency(stats?.remaining),
      icon: DollarSign,
      color: 'bg-red-500',
      textColor: 'text-red-500'
    }
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">{t('admin_dashboard')}</h1>
          <p className="text-[#4A5D43] text-sm mt-1">{getDateRangeLabel()} statistikası</p>
        </div>

        {/* Date Range Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#4A5D43]" />
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-40" data-testid="date-range-select">
                <SelectValue placeholder="Tarix seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Bütün vaxt</SelectItem>
                <SelectItem value="today">Bugün</SelectItem>
                <SelectItem value="week">Son 7 gün</SelectItem>
                <SelectItem value="month">Bu ay</SelectItem>
                <SelectItem value="3months">Son 3 ay</SelectItem>
                <SelectItem value="6months">Son 6 ay</SelectItem>
                <SelectItem value="year">Bu il</SelectItem>
                <SelectItem value="custom">Xüsusi tarix</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showCustomDates && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-36"
                data-testid="custom-start-date"
              />
              <span className="text-[#4A5D43]">-</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-36"
                data-testid="custom-end-date"
              />
              <Button 
                onClick={fetchStats} 
                size="sm"
                className="bg-[#2D5016] hover:bg-[#1A2415]"
                data-testid="apply-filter-btn"
              >
                Tətbiq et
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016]"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {statCards.map((card, index) => (
              <div key={index} className="stat-card" data-testid={`stat-card-${index}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#4A5D43] text-sm mb-1">{card.title}</p>
                    <p className={`text-xl md:text-2xl font-semibold ${card.textColor}`}>{card.value}</p>
                  </div>
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${card.color} flex items-center justify-center`}>
                    <card.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-[#1A2415] mb-4">Sürətli Əməliyyatlar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <a 
                href="/admin/reservations" 
                className="p-4 bg-white border border-[#E5E3DB] rounded-lg hover:shadow-md transition-shadow flex items-center gap-3"
                data-testid="quick-action-reservations"
              >
                <Calendar className="w-8 h-8 text-[#D97706]" />
                <div>
                  <p className="font-medium text-[#1A2415]">{t('reservation_new')}</p>
                  <p className="text-xs text-[#4A5D43]">Yeni rezervasiya yarat</p>
                </div>
              </a>
              <a 
                href="/admin/cottages" 
                className="p-4 bg-white border border-[#E5E3DB] rounded-lg hover:shadow-md transition-shadow flex items-center gap-3"
                data-testid="quick-action-cottages"
              >
                <Home className="w-8 h-8 text-[#2D5016]" />
                <div>
                  <p className="font-medium text-[#1A2415]">{t('cottage_add')}</p>
                  <p className="text-xs text-[#4A5D43]">Yeni kottec əlavə et</p>
                </div>
              </a>
              <a 
                href="/admin/customers" 
                className="p-4 bg-white border border-[#E5E3DB] rounded-lg hover:shadow-md transition-shadow flex items-center gap-3"
                data-testid="quick-action-customers"
              >
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium text-[#1A2415]">{t('customer_list')}</p>
                  <p className="text-xs text-[#4A5D43]">Müştəriləri gör</p>
                </div>
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
